import formatMomentLabel from '../formatMomentLabel';
import { submissionKey } from './countSubmissions';
import toMillis from '../measurementSignals/toMillis';

/**
 * @typedef {object} MomentResponseSummary
 * @property {number|null} position - Measure-moment position (null = unresolved moment).
 * @property {string} label - Display label, e.g. "Voormeting (1)".
 * @property {number} respondents - Unique respondents at this moment.
 * @property {number} answers - Total response records at this moment.
 */

/**
 * @typedef {object} ResponsesSummary
 * @property {number} totalRespondents - Unique respondents across all moments.
 * @property {number} totalAnswers - Total response records across all moments.
 * @property {MomentResponseSummary[]} perMoment - Breakdown per measure moment, sorted by position.
 */

/**
 * Summarize response records into respondents + answers, grouped per measure moment.
 *
 * Respondents are deduped by `submissionKey` (FormID → SessionID → the
 * millisecond timestamp), the one rule shared with `countSubmissions`;
 * answers count every record. Submissions from before late 2024 carry no
 * FormID — keying on FormID alone showed them as 0 respondents.
 * AI-CONTEXT: See docs/pitfalls.md#database-totaldocs-overcounts-respondents
 * NOTE: these are unique *submissions* — a person who answers at two moments
 * counts once per moment AND twice in the global total.
 *
 * Each record's moment is resolved from `MomentPosition` (set by the hierarchy
 * Cloud Function so synced child-project moments align by position), falling
 * back to `id2pos[MomentID]` for local (project-tenant) records.
 * Every position in `id2pos` gets a row, also when no record lands on it, so a
 * moment that was never filled in shows as 0 instead of vanishing.
 *
 * @param {Array<Object>} records - Response records.
 * @param {object} [options]
 * @param {Object<string, number>} [options.id2pos] - MomentID → Position map: resolves records lacking MomentPosition, and lists every moment that gets a row.
 * @param {Object<number, string>} [options.pos2title] - Position → Title map for labels.
 * @returns {ResponsesSummary}
 */
const summarizeRespondentsByMoment = (records, { id2pos = {}, pos2title = {} } = {}) => {
    const list = Array.isArray(records) ? records : [];

    const totalRespondentSet = new Set();
    /** @type {Map<number|string, { position: number|null, answers: number, respondentSet: Set<string> }>} */
    const byPosition = new Map();
    const NONE_KEY = '__none__';

    // Elk bekend meetmoment krijgt een rij, ook zonder antwoorden: een moment
    // dat nooit is ingevuld moet als 0 zichtbaar zijn, niet verdwijnen.
    Object.values(id2pos).forEach((raw) => {
        const position = typeof raw === 'number' ? raw : parseInt(raw, 10);
        if (Number.isNaN(position) || byPosition.has(position)) return;
        byPosition.set(position, { position, answers: 0, respondentSet: new Set() });
    });

    list.forEach((record, index) => {
        const respondentId = submissionKey(record, toMillis(record?.Timestamp), index);
        totalRespondentSet.add(respondentId);

        let position = record?.MomentPosition;
        if (position == null) position = id2pos[record?.MomentID];
        if (typeof position !== 'number') position = parseInt(position, 10);
        if (Number.isNaN(position)) position = null;

        const key = position == null ? NONE_KEY : position;
        if (!byPosition.has(key)) {
            byPosition.set(key, { position, answers: 0, respondentSet: new Set() });
        }
        const bucket = byPosition.get(key);
        bucket.answers += 1;
        bucket.respondentSet.add(respondentId);
    });

    const perMoment = Array.from(byPosition.values())
        .sort((a, b) => {
            if (a.position == null) return 1;
            if (b.position == null) return -1;
            return a.position - b.position;
        })
        .map((bucket) => ({
            position: bucket.position,
            label: bucket.position == null
                ? 'Onbekend meetmoment'
                : formatMomentLabel(pos2title[bucket.position], bucket.position),
            respondents: bucket.respondentSet.size,
            answers: bucket.answers,
        }));

    return {
        totalRespondents: totalRespondentSet.size,
        totalAnswers: list.length,
        perMoment,
    };
};

export default summarizeRespondentsByMoment;
